import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGoogleMaps } from '../contexts/GoogleMapsContext';

interface Place {
  name: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

interface LocationAutocompleteProps {
  value: string;
  onChange: (place: Place) => void;
  error?: string;
}

export function LocationAutocomplete({ value, onChange, error }: LocationAutocompleteProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [loading, setLoading] = useState(false);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isLoaded, loadError } = useGoogleMaps();

  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;

    const options = {
      types: ['establishment'],
      componentRestrictions: { country: 'ch' }, // Restrict to Switzerland
    };

    autocompleteRef.current = new google.maps.places.Autocomplete(
      inputRef.current,
      options
    );

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current?.getPlace();
      
      if (place?.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        // Format the location string
        let locationName = place.name || '';
        let city = '';

        // Extract city from address components
        place.address_components?.forEach(component => {
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
        });

        // Combine venue name and city
        const formattedLocation = city ? `${locationName}, ${city}` : locationName;
        
        onChange({
          name: formattedLocation,
          coordinates: {
            latitude: lat,
            longitude: lng,
          },
        });
        setInputValue(formattedLocation);
      }
    });

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isLoaded, onChange]);

  if (loadError) {
    return (
      <div className="text-red-500">
        Error loading Google Maps API
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light/40" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={t('forms.searchVenue')}
          className="w-full pl-10 pr-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
          disabled={!isLoaded}
        />
        {!isLoaded && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light/40 animate-spin" />
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}
      <div className="absolute w-full z-[100]"></div>
    </div>
  );
}